import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Camera, Upload, Trash2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import HamburgerMenu from '../components/HamburgerMenu';

const ProgressPhotos = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState({ before: null, current: null });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(null); // 'before' or 'current'
  const [notes, setNotes] = useState('');
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    loadPhotos();
  }, []);

  useEffect(() => {
    // Cleanup camera when component unmounts or camera closes
    return () => {
      stopCamera();
    };
  }, []);

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/progress/photos`);
      setPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async (photoType) => {
    setShowCamera(photoType);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Failed to access camera. Please check permissions.');
      setShowCamera(null);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(null);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.8);

    // Upload
    await uploadPhoto(showCamera, imageData);
    
    // Stop camera
    stopCamera();
  };

  const handleFileUpload = async (photoType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      await uploadPhoto(photoType, reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadPhoto = async (photoType, imageData) => {
    setUploading(true);
    try {
      await axios.post(`${API}/progress/photos/upload`, {
        photo_type: photoType,
        image_data: imageData,
        notes: notes
      });
      
      toast.success(`${photoType === 'before' ? 'Before' : 'Current'} photo uploaded!`);
      setNotes('');
      await loadPhotos();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (photoType) => {
    if (!confirm(`Delete ${photoType} photo? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/progress/photos/${photoType}`);
      toast.success('Photo deleted');
      await loadPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const retakeBeforePhoto = async () => {
    if (!confirm('Retake "Before" photo? Your current Before photo will be replaced.')) return;
    startCamera('before');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black pb-20 overflow-x-hidden">
      <HamburgerMenu />
      
      <div className="max-w-6xl mx-auto px-4 py-4 animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 tracking-wider">TRANSFORMATION GALLERY</h1>
          <p className="text-stone-500 uppercase tracking-widest text-xs mt-1">Chronicle your conquest</p>
        </div>

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="glass rounded-xl max-w-2xl w-full p-6 border border-stone-800">
              <h2 className="text-2xl font-bold text-blood-400 mb-4 tracking-wider uppercase">
                Take {showCamera === 'before' ? 'Before' : 'Current'} Photo
              </h2>
              
              <div className="relative mb-4">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full rounded-lg bg-black border-2 border-stone-700"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={capturePhoto}
                  disabled={uploading}
                  className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Capture Photo'}
                </Button>
                <Button
                  variant="outline"
                  onClick={stopCamera}
                  disabled={uploading}
                  className="glass border-red-700 text-red-400 hover:bg-red-900/20 font-semibold"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <Card className="glass p-8 text-center border border-stone-800">
            <p className="text-blood-400 text-xl font-bold tracking-wider">LOADING YOUR TRANSFORMATION...</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Photo Comparison */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Before Photo */}
              <Card className="glass p-6 border border-stone-800">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-blood-400 tracking-wider uppercase">Before</h2>
                  {photos.before && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => retakeBeforePhoto()}
                      className="text-blood-400 hover:text-blood-500"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Retake
                    </Button>
                  )}
                </div>

                {photos.before ? (
                  <div className="space-y-3">
                    <img
                      src={photos.before.image_data}
                      alt="Before"
                      className="w-full h-96 object-cover rounded-lg border-2 border-stone-800"
                    />
                    <p className="text-blood-400 text-sm font-semibold">
                      Taken: {new Date(photos.before.date_taken).toLocaleDateString()}
                    </p>
                    {photos.before.notes && (
                      <p className="text-stone-300 text-sm">{photos.before.notes}</p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deletePhoto('before')}
                      className="w-full glass border-red-700 text-red-400 hover:bg-red-900/20 font-semibold"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Camera className="w-16 h-16 text-blood-500 mx-auto mb-4" />
                    <p className="text-stone-400 mb-4 uppercase tracking-wider">No before photo yet</p>
                    <div className="space-y-2">
                      <Button
                        onClick={() => startCamera('before')}
                        className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Take Photo
                      </Button>
                      <label className="block">
                        <Button
                          as="span"
                          variant="outline"
                          className="w-full glass border-stone-700 text-stone-400 cursor-pointer hover:bg-stone-800"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photo
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload('before', e)}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </Card>

              {/* Current Photo */}
              <Card className="glass p-6 border border-stone-800">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-blood-400 tracking-wider uppercase">Current</h2>
                </div>

                {photos.current ? (
                  <div className="space-y-3">
                    <img
                      src={photos.current.image_data}
                      alt="Current"
                      className="w-full h-96 object-cover rounded-lg border-2 border-stone-800"
                    />
                    <p className="text-blood-400 text-sm font-semibold">
                      Taken: {new Date(photos.current.date_taken).toLocaleDateString()}
                    </p>
                    {photos.current.notes && (
                      <p className="text-stone-300 text-sm">{photos.current.notes}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => startCamera('current')}
                        className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-800 hover:from-emerald-500 hover:to-emerald-700 text-white font-bold tracking-wider"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Update
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deletePhoto('current')}
                        className="glass border-red-700 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Camera className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <p className="text-stone-400 mb-4 uppercase tracking-wider">No current photo yet</p>
                    <div className="space-y-2">
                      <Button
                        onClick={() => startCamera('current')}
                        className="w-full bg-gradient-to-r from-emerald-600 to-emerald-800 hover:from-emerald-500 hover:to-emerald-700 text-white font-bold tracking-wider"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Take Photo
                      </Button>
                      <label className="block">
                        <Button
                          as="span"
                          variant="outline"
                          className="w-full glass border-stone-700 text-stone-400 cursor-pointer hover:bg-stone-800"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photo
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload('current', e)}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Tips */}
            <Card className="glass p-6 border border-stone-800">
              <h3 className="text-lg font-bold text-blood-400 mb-3 tracking-wider uppercase">📸 Photo Tips</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-stone-400">
                <div>• Take photos in the same location</div>
                <div>• Use consistent lighting</div>
                <div>• Wear similar clothing</div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressPhotos;
